// src/components/OrderManagement.js
import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { db } from '../firebaseConfig';
import {
  collection,
  query,
  where,
  onSnapshot,
  addDoc,
  deleteDoc,
  doc,
  orderBy,
  serverTimestamp,
  Timestamp,
} from 'firebase/firestore';
import { useTranslation } from 'react-i18next'; // <-- MODIFICA: Importa hook

// --- IMPORT MUI ---
import {
  Box,
  Typography, 
  IconButton,
  CircularProgress, Paper, Button, TextField, List,
  ListItem, ListItemText, ListItemIcon, 
  Chip, Autocomplete, 
  ListItemButton,
  ToggleButton,
  ToggleButtonGroup
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import DeleteIcon from '@mui/icons-material/Delete';
import LocalShippingIcon from '@mui/icons-material/LocalShipping';
import ArchiveIcon from '@mui/icons-material/Archive';

// --- MODIFICA: Helper (con t() e lingua) ---
const formatDate = (date, lang) => { 
  if(!date)return'N/A'; 
  const d=date.toDate?date.toDate():new Date(date);
  const locale = lang === 'it' ? 'it-IT' : 'en-GB'; // Usa en-GB per formato DD/MM/YYYY
  return d.toLocaleDateString(locale,{day:'2-digit',month:'2-digit',year:'numeric'}); 
};

const getStatusChipColor = (status) => { 
  if (status === 'Received') return 'success';
  if (status === 'Shipped') return 'info';
  if (status === 'Ordered') return 'primary'; 
  return 'warning'; // Pending
};

// Funzione helper per tradurre lo stato
const translateStatus = (status, t) => {
  const key = `status_${status.toLowerCase()}`;
  const translated = t(key);
  // Se la traduzione non esiste, ritorna la chiave originale
  return translated === key ? status : translated;
}
// --- FINE MODIFICA ---

function OrderManagement({ teamId, userRole }) {
  // --- MODIFICA: Inizializza t() ---
  const { t, i18n } = useTranslation('order');
  const currentLang = i18n.language;
  // --- FINE MODIFICA ---

  const navigate = useNavigate();
  // Stati (invariati)
  const [orders, setOrders] = useState([]);
  const [isLoadingOrders, setIsLoadingOrders] = useState(true);
  const [suppliers, setSuppliers] = useState([]);
  const [isLoadingSuppliers, setIsLoadingSuppliers] = useState(true);
  const [selectedSupplier, setSelectedSupplier] = useState(null);
  const [orderDate, setOrderDate] = useState(new Date().toISOString().split('T')[0]);
  const [notes, setNotes] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const canManage = userRole === 'admin' || userRole === 'Engineer' || userRole === 'Magazziniere';
  const [viewMode, setViewMode] = useState('active');

  // Handler (invariato)
  const handleViewChange = (event, newView) => {
    if (newView !== null) {
      setViewMode(newView);
    }
  };

  // useEffects (invariati)
  useEffect(() => {
    if (!teamId) { setIsLoadingSuppliers(false); return; }
    setIsLoadingSuppliers(true);
    const q = query(collection(db, "suppliers"), where("teamId", "==", teamId), orderBy("companyName", "asc"));
    const unsub = onSnapshot(q, (qs) => { setSuppliers(qs.docs.map(d => ({ id: d.id, ...d.data() }))); setIsLoadingSuppliers(false); }, (err) => { setIsLoadingSuppliers(false); });
    return () => unsub();
  }, [teamId]);

  useEffect(() => {
    if (!teamId) { setIsLoadingOrders(false); return; }
    setIsLoadingOrders(true);
    const q = query(collection(db, "orders"), where("teamId", "==", teamId), orderBy("createdAt", "desc"));
    
    const unsub = onSnapshot(q, (qs) => { 
        setOrders(qs.docs.map(doc => ({ id: doc.id, ...doc.data() }))); 
        setIsLoadingOrders(false); 
    }, (err) => { 
        console.error("Error order listener (Check Firestore Index):", err.message);
        setIsLoadingOrders(false);
    });
    return () => unsub();
  }, [teamId]);

  // --- MODIFICA: Funzioni Handler (con t() per alert) ---
  const handleCreateOrder = async (e) => {
    e.preventDefault();
    if (!selectedSupplier || !orderDate) { 
      alert(t('error_supplier_date')); // Tradotto
      return; 
    }
    setIsSubmitting(true);
    try {
      const dateObject = new Date(orderDate); dateObject.setMinutes(dateObject.getMinutes() + dateObject.getTimezoneOffset());
      const orderData = {
        teamId: teamId, supplierId: selectedSupplier.id, supplierName: selectedSupplier.companyName,
        orderDate: Timestamp.fromDate(dateObject), notes: notes.trim(), status: 'Pending',
        items: [], createdAt: serverTimestamp(),
        isArchived: false
      };
      const docRef = await addDoc(collection(db, "orders"), orderData);
      navigate(`/order/${docRef.id}`);
    } catch (error) { 
        console.error("Error creating order:", error);
        alert(t('error_create')); // Tradotto
        setIsSubmitting(false);
    }
  };

  const handleDeleteOrder = async (e, orderId, orderStatus) => {
    e.stopPropagation();
    if (orderStatus !== 'Pending') { 
      alert(t('delete_error_status')); // Tradotto
      return; 
    }
    if (window.confirm(t('delete_confirm'))) { // Tradotto
      try { await deleteDoc(doc(db, "orders", orderId)); }
      catch (error) { 
        console.error("Error deleting order:", error); 
        alert(t('delete_error')); // Tradotto
      }
    }
  };
  // --- FINE MODIFICA ---
  

  if (isLoadingOrders || isLoadingSuppliers) {
    return ( <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '80vh' }}><CircularProgress /></Box> );
  }

  const activeOrders = orders.filter(o => !o.isArchived);
  const archivedOrders = orders.filter(o => o.isArchived === true);
  const ordersToShow = viewMode === 'active' ? activeOrders : archivedOrders;


  // --- RENDER (MODIFICATO con t()) ---
  return (
    <>
      {/* 1. Header */}
      <Paper elevation={0} sx={{ p: 3, mb: 3, backgroundColor: 'background.paper', border: '1px solid', borderColor: 'divider' }}>
        <Typography variant="h4" component="h1" sx={{ fontWeight: 700 }}>
          {t('title')} {/* Tradotto */}
        </Typography>
      </Paper>

      {/* 2. Paper Creazione Ordine */}
      {canManage && (
        <Paper 
          elevation={0} 
          sx={{ 
            p: { xs: 2, sm: 3 }, 
            mb: 3, 
            border: '1px solid', 
            borderColor: 'divider', 
            backgroundColor: 'background.paper' 
          }}
        >
          <Box component="form" onSubmit={handleCreateOrder} sx={{ mb: 1 }}>
            <Typography variant="h5" gutterBottom>{t('add_title')}</Typography>
            <Box sx={{ display: 'flex', flexDirection: { xs: 'column', md: 'row' }, gap: 2.5, mb: 2.5 }}>
              <Autocomplete 
                options={suppliers} 
                getOptionLabel={(o) => o.companyName} 
                value={selectedSupplier} 
                onChange={(e, val) => setSelectedSupplier(val)} 
                isOptionEqualToValue={(o, v) => o.id === v.id} 
                renderInput={(params) => (<TextField {...params} label={t('supplier_label')} required />)}
                disabled={isLoadingSuppliers} 
                fullWidth 
                sx={{ flex: 2 }}
              />
              <TextField 
                label={t('date_label')}
                type="date" 
                value={orderDate} 
                onChange={(e) => setOrderDate(e.target.value)} 
                required 
                fullWidth 
                InputLabelProps={{ shrink: true }} 
                sx={{ flex: 1 }}
              />
            </Box>
            <Box sx={{ display: 'flex', mb: 2.5 }}>
              <TextField 
                label={t('notes_label')}
                value={notes} 
                onChange={(e) => setNotes(e.target.value)} 
                fullWidth 
                multiline 
                rows={3}
              />
            </Box>
            <Button 
              type="submit" 
              variant="contained" 
              disabled={isSubmitting || isLoadingSuppliers} 
              startIcon={isSubmitting ? <CircularProgress size={20} /> : <AddIcon />} 
              size="large"
              sx={{ mt: 1, height: '56px' }}
            >
              {t('create_button')}
            </Button>
          </Box>
        </Paper>
      )}
      
      {/* 3. Paper Lista Ordini */}
      <Paper 
        elevation={0} 
        sx={{ 
          p: { xs: 2, sm: 3 }, 
          border: '1px solid', 
          borderColor: 'divider', 
          backgroundColor: 'background.paper' 
        }}
      >
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2, flexWrap: 'wrap', gap: 2 }}>
          <Typography variant="h5" gutterBottom sx={{ mb: 0 }}>
            {viewMode === 'active' ? t('list_title_active') : t('list_title_archived')}
          </Typography>
          <ToggleButtonGroup
            color="primary"
            value={viewMode}
            exclusive
            onChange={handleViewChange}
            aria-label="View orders"
          >
            <ToggleButton value="active">{t('toggle_active')}</ToggleButton>
            <ToggleButton value="archived">{t('toggle_archived')}</ToggleButton>
          </ToggleButtonGroup>
        </Box>

        {(isLoadingOrders || isLoadingSuppliers) && <CircularProgress size={24} />}
        
        {!isLoadingOrders && orders.length === 0 && ( <Typography variant="body2" sx={{ fontStyle: 'italic' }}>{t('empty_all')}</Typography> )}
        {!isLoadingOrders && orders.length > 0 && ordersToShow.length === 0 && (
          <Typography variant="body2" sx={{ fontStyle: 'italic' }}>
            {viewMode === 'active' ? t('empty_active') : t('empty_archived')}
          </Typography>
        )}

        {!isLoadingOrders && ordersToShow.length > 0 && (
          <List>
            {ordersToShow.map((order) => (
              <ListItem 
                key={order.id} 
                disablePadding 
                divider 
                sx={{ 
                  backgroundColor: 'background.default',
                  border: '1px solid',
                  borderColor: 'divider',
                  borderRadius: 2,
                  mb: 1
                }}
                secondaryAction={ 
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, pr: 2 }}>
                    <Chip label={translateStatus(order.status, t)} color={getStatusChipColor(order.status)} size="small" />
                    {(canManage && order.status === 'Pending') && (
                      <IconButton edge="end" aria-label="delete" onClick={(e) => handleDeleteOrder(e, order.id, order.status)}> 
                        <DeleteIcon color='error' /> 
                      </IconButton> 
                    )}
                  </Box>
                }
              >
                <ListItemButton onClick={() => navigate(`/order/${order.id}`)} sx={{ borderRadius: 2 }}>
                  <ListItemIcon sx={{ minWidth: 40 }}>
                    {order.isArchived ? <ArchiveIcon color="action" /> : <LocalShippingIcon />}
                  </ListItemIcon>
                  <ListItemText 
                    primary={order.supplierName} 
                    secondary={`${t('card_date')} ${formatDate(order.orderDate, currentLang)} - ${t('card_items')} ${(order.items || []).length}`} 
                    primaryTypographyProps={{ fontWeight: 600 }}
                  />
                </ListItemButton>
              </ListItem>
            ))}
          </List>
        )}
      </Paper>
    </>
  );
}

export default OrderManagement;